import { useCallback } from "react";

import { type BulkPayloadItem, parseBatchResult } from "~/helpers/grid/batchLifecycleHelpers";
import logger from "~/helpers/logger";
import mutationOutcome, { type MutationStageEvidence, type MutationStageOutcome } from "~/helpers/mutationOutcome";
import mutationRunner, {
	MutationErrorMessage,
	MutationPayloadWithErrors,
	MutationRawResult,
	MutationRequest,
	MutationValidationResult,
	MutationWriteResult,
	RunMutationOptions,
	RunMutationResultOptions,
	RunMutationValidationOptions
} from "~/helpers/runMutation";
import { type BatchAction, type BatchResult } from "~/hooks/useBatchLifecycle";
import { useSnacks } from "~/providers/SnackProvider";

interface BatchActionPayload {
	errors?: Array<{ message: string }> | null;
}

export interface UseMutationRunnerReturn {
	/** Resolves true on success; on transport error or payload errors, logs and snacks errorMessage, then resolves false. */
	run: <TData, TPayload extends MutationPayloadWithErrors>(
		request: MutationRequest<TData>,
		selectPayload: (data: TData) => TPayload | null | undefined,
		errorMessage: MutationErrorMessage<TPayload>,
		options?: RunMutationOptions
	) => Promise<boolean>;
	/** Resolves the entity data selected by selectEntity, or null after error handling. A missing entity is treated as a failure; successMessage is snacked only after the entity check passes. */
	runForEntity: <TData, TPayload extends MutationPayloadWithErrors, TEntity>(
		request: MutationRequest<TData>,
		selectPayload: (data: TData) => TPayload | null | undefined,
		selectEntity: (payload: TPayload) => TEntity | null | undefined,
		errorMessage: MutationErrorMessage<TPayload>,
		options?: RunMutationOptions,
		queuedEntity?: TEntity
	) => Promise<TEntity | null>;
	/** Runs a server-side bulk mutation and folds the payload into a BatchResult via parseBatchResult. Snacks a generic network error only on transport failure, unless options.silent is set; per-item errors are surfaced through the BatchResult, not snacked. */
	runForBatch: <TData, TPayload extends BatchActionPayload>(
		request: MutationRequest<TData>,
		selectPayload: (data: TData) => TPayload | null | undefined,
		selectItems: (payload: TPayload) => BulkPayloadItem[] | null | undefined,
		labels: string[],
		action: BatchAction,
		options?: { silent?: boolean }
	) => Promise<BatchResult>;
	runForWrite: <TData, TPayload extends MutationPayloadWithErrors>(
		request: MutationRequest<TData>,
		selectPayload: (data: TData) => TPayload | null | undefined,
		errorMessage: MutationErrorMessage<TPayload>,
		options?: RunMutationOptions
	) => Promise<MutationWriteResult>;
	/** Resolves the raw result after the shared guard/logger/snack handling. error conflates both failure kinds; transportError and payloadErrors keep them apart for callers (e.g. queue drainers) that must classify the failure themselves. */
	runForResult: <TData, TPayload extends MutationPayloadWithErrors>(
		request: MutationRequest<TData>,
		selectPayload: (data: TData) => TPayload | null | undefined,
		errorMessage: MutationErrorMessage<TPayload>,
		options?: RunMutationResultOptions<TPayload>
	) => Promise<MutationRawResult<TData>>;
	runForStage: <TData, TPayload extends MutationPayloadWithErrors, TStage extends string>(
		request: MutationRequest<TData>,
		selectPayload: (data: TData) => TPayload | null | undefined,
		errorMessage: MutationErrorMessage<TPayload>,
		evidence: MutationStageEvidence<TPayload, TStage>,
		options?: Pick<RunMutationResultOptions<TPayload>, "expectedError" | "expectedRefusal">
	) => Promise<MutationStageOutcome<TStage>>;
	/** For form mutations: payload validation errors are returned silently for inline rendering (never snacked); transport errors and a missing entity log and snack unless silent; successMessage is snacked only on success. */
	runForValidation: <TData, TPayload extends MutationPayloadWithErrors, TEntity>(
		request: MutationRequest<TData>,
		selectPayload: (data: TData) => TPayload | null | undefined,
		selectEntity: (payload: TPayload) => TEntity | null | undefined,
		errorMessage: MutationErrorMessage<TPayload>,
		options?: RunMutationValidationOptions
	) => Promise<MutationValidationResult<TPayload, TEntity>>;
	/** Resolves the payload on success; on failure logs and throws Error(resolved message) — never snacks. For batch/orchestrated flows (e.g. CSV imports) where the caller owns error presentation. */
	runOrThrow: <TData, TPayload extends MutationPayloadWithErrors>(
		request: MutationRequest<TData>,
		selectPayload: (data: TData) => TPayload | null | undefined,
		errorMessage: MutationErrorMessage<TPayload>,
		options?: RunMutationOptions
	) => Promise<TPayload | null>;
}

const useMutationRunner = (): UseMutationRunnerReturn => {
	const [openSnack] = useSnacks();

	const run = useCallback(
		<TData, TPayload extends MutationPayloadWithErrors>(
			request: MutationRequest<TData>,
			selectPayload: (data: TData) => TPayload | null | undefined,
			errorMessage: MutationErrorMessage<TPayload>,
			options?: RunMutationOptions
		): Promise<boolean> => mutationRunner.runMutation({ errorMessage, openSnack, options, request, selectPayload }),
		[openSnack]
	);

	const runForEntity = useCallback(
		<TData, TPayload extends MutationPayloadWithErrors, TEntity>(
			request: MutationRequest<TData>,
			selectPayload: (data: TData) => TPayload | null | undefined,
			selectEntity: (payload: TPayload) => TEntity | null | undefined,
			errorMessage: MutationErrorMessage<TPayload>,
			options?: RunMutationOptions,
			queuedEntity?: TEntity
		): Promise<TEntity | null> =>
			mutationRunner.runMutationForEntity({
				errorMessage,
				openSnack,
				options,
				queuedEntity,
				request,
				selectEntity,
				selectPayload
			}),
		[openSnack]
	);

	const runForBatch = useCallback(
		async <TData, TPayload extends BatchActionPayload>(
			request: MutationRequest<TData>,
			selectPayload: (data: TData) => TPayload | null | undefined,
			selectItems: (payload: TPayload) => BulkPayloadItem[] | null | undefined,
			labels: string[],
			action: BatchAction,
			options?: { silent?: boolean }
		): Promise<BatchResult> => {
			const result = await request;
			if (result.error) {
				logger.error(`Batch ${action} failed`, result.error);
				if (!options?.silent) openSnack(`Network error during batch ${action}`, "error");
				return { action, failedCount: labels.length, failures: ["Network error"], totalCount: labels.length };
			}
			const payload = result.data ? selectPayload(result.data) : undefined;
			return parseBatchResult(payload ? selectItems(payload) : undefined, payload?.errors, labels, action);
		},
		[openSnack]
	);

	const runForResult = useCallback(
		<TData, TPayload extends MutationPayloadWithErrors>(
			request: MutationRequest<TData>,
			selectPayload: (data: TData) => TPayload | null | undefined,
			errorMessage: MutationErrorMessage<TPayload>,
			options?: RunMutationResultOptions<TPayload>
		): Promise<MutationRawResult<TData>> =>
			mutationRunner.runMutationForResult({ errorMessage, openSnack, options, request, selectPayload }),
		[openSnack]
	);

	const runForWrite = useCallback(
		<TData, TPayload extends MutationPayloadWithErrors>(
			request: MutationRequest<TData>,
			selectPayload: (data: TData) => TPayload | null | undefined,
			errorMessage: MutationErrorMessage<TPayload>,
			options?: RunMutationOptions
		): Promise<MutationWriteResult> =>
			mutationRunner.runMutationForWrite({ errorMessage, openSnack, options, request, selectPayload }),
		[openSnack]
	);

	const runForStage = useCallback(
		async <TData, TPayload extends MutationPayloadWithErrors, TStage extends string>(
			request: MutationRequest<TData>,
			selectPayload: (data: TData) => TPayload | null | undefined,
			errorMessage: MutationErrorMessage<TPayload>,
			evidence: MutationStageEvidence<TPayload, TStage>,
			options?: Pick<RunMutationResultOptions<TPayload>, "expectedError" | "expectedRefusal">
		): Promise<MutationStageOutcome<TStage>> => {
			const raw = await mutationRunner.runMutationForResult({
				errorMessage,
				openSnack,
				options: { ...options, silent: true },
				request,
				selectPayload
			});
			return mutationOutcome.classifyRawResult(raw, selectPayload, evidence);
		},
		[openSnack]
	);

	const runForValidation = useCallback(
		<TData, TPayload extends MutationPayloadWithErrors, TEntity>(
			request: MutationRequest<TData>,
			selectPayload: (data: TData) => TPayload | null | undefined,
			selectEntity: (payload: TPayload) => TEntity | null | undefined,
			errorMessage: MutationErrorMessage<TPayload>,
			options?: RunMutationValidationOptions
		): Promise<MutationValidationResult<TPayload, TEntity>> =>
			mutationRunner.runMutationForValidation({
				errorMessage,
				openSnack,
				options,
				request,
				selectEntity,
				selectPayload
			}),
		[openSnack]
	);

	const runOrThrow = useCallback(
		<TData, TPayload extends MutationPayloadWithErrors>(
			request: MutationRequest<TData>,
			selectPayload: (data: TData) => TPayload | null | undefined,
			errorMessage: MutationErrorMessage<TPayload>,
			options?: RunMutationOptions
		): Promise<TPayload | null> =>
			mutationRunner.runMutationOrThrow({ errorMessage, openSnack, options, request, selectPayload }),
		[openSnack]
	);

	return { run, runForBatch, runForEntity, runForResult, runForStage, runForValidation, runForWrite, runOrThrow };
};

export default useMutationRunner;
